// components/MainSelectionPage.tsx
import React from 'react';
import { ModernCard } from './ui/ModernCard.tsx';
import { ModernButton } from './ui/ModernButton.tsx';

interface MainSelectionPageProps {
  onSelectTaskPurchase: () => void;
  onSelectBooking: () => void;
}

export const MainSelectionPage: React.FC<MainSelectionPageProps> = ({
  onSelectTaskPurchase,
  onSelectBooking
}) => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
      {/* Header - Compact on mobile */}
      <div className="text-center mb-6 sm:mb-12">
        <h1 className="text-xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-4">
          مرحباً بك في منصتنا التعليمية
        </h1>
        <p className="text-sm sm:text-lg md:text-xl text-gray-600">
          اختر الخدمة التي تحتاجها
        </p>
      </div>

      {/* Service Selection Cards - Both visible on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8">
        {/* Assignment Solution Card */}
        <ModernCard className="p-4 sm:p-6 md:p-8 hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-blue-200 touch-manipulation">
          <div className="text-center">
            <div className="text-3xl sm:text-4xl md:text-6xl mb-3 sm:mb-4 md:mb-6">📝</div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">
              حل واجب / Assignment
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-3 sm:mb-4 md:mb-6 leading-relaxed">
              احصل على حل واجباتك من خبراء متخصصين
            </p>
            
            {/* Features - Only on larger screens */}
            <div className="hidden md:block space-y-2 mb-6">
              <div className="flex items-center justify-start text-sm text-gray-700">
                <span className="text-green-600 ml-2">✅</span>
                حلول مخصصة لجميع المواد
              </div>
              <div className="flex items-center justify-start text-sm text-gray-700">
                <span className="text-green-600 ml-2">✅</span>
                جودة عالية ومواعيد محددة
              </div>
              <div className="flex items-center justify-start text-sm text-gray-700">
                <span className="text-green-600 ml-2">✅</span>
                أسعار تنافسية ودفع آمن
              </div>
            </div>

            {/* Compact features for mobile/tablet */}
            <div className="md:hidden mb-3 text-xs sm:text-sm text-gray-600">
              <p>✅ جودة عالية • دفع آمن</p>
            </div>

            <ModernButton
              variant="primary"
              onClick={onSelectTaskPurchase}
              className="w-full text-sm sm:text-base md:text-lg py-2.5 sm:py-3 md:py-4"
            >
              ابدأ الآن →
            </ModernButton>
          </div>
        </ModernCard>

        {/* Exam Session Card */}
        <ModernCard className="p-4 sm:p-6 md:p-8 hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-transparent hover:border-green-200 touch-manipulation">
          <div className="text-center">
            <div className="text-3xl sm:text-4xl md:text-6xl mb-3 sm:mb-4 md:mb-6">📅</div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">
              حجز اختبار
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-3 sm:mb-4 md:mb-6 leading-relaxed">
              احجز جلسة مع مدرسين متخصصين
            </p>
            
            {/* Features - Only on larger screens */}
            <div className="hidden md:block space-y-2 mb-6">
              <div className="flex items-center justify-start text-sm text-gray-700">
                <span className="text-green-600 ml-2">✅</span>
                جلسات اختبار مباشرة ومخصصة
              </div>
              <div className="flex items-center justify-start text-sm text-gray-700">
                <span className="text-green-600 ml-2">✅</span>
                مدرسين معتمدين ومتخصصين
              </div>
              <div className="flex items-center justify-start text-sm text-gray-700">
                <span className="text-green-600 ml-2">✅</span>
                مواعيد مرنة تناسب جدولك
              </div>
            </div>

            {/* Compact features for mobile/tablet */}
            <div className="md:hidden mb-3 text-xs sm:text-sm text-gray-600">
              <p>✅ مدرسين معتمدين • مواعيد مرنة</p>
            </div>

            <ModernButton
              variant="success"
              onClick={onSelectBooking}
              className="w-full text-sm sm:text-base md:text-lg py-2.5 sm:py-3 md:py-4"
            >
              احجز الآن →
            </ModernButton>
          </div>
        </ModernCard>
      </div>

      {/* Help Section - Simplified for all screens */}
      <div className="text-center">
        <ModernCard className="p-3 sm:p-4 md:p-6 bg-blue-50 border-blue-200">
          <h3 className="text-sm sm:text-base md:text-lg font-semibold text-blue-900 mb-2">
            🤔 غير متأكد من الخيار المناسب؟
          </h3>
          
          <div className="text-xs sm:text-sm text-blue-700">
            <span className="font-medium">حل واجب:</span> للحصول على حلول كاملة • 
            <span className="font-medium"> حجز اختبار:</span> للمساعدة المباشرة
          </div>
        </ModernCard>
      </div>

      {/* Statistics - More compact on mobile */}
      <div className="mt-6 sm:mt-8 md:mt-12 grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 text-center">
        <div className="space-y-1">
          <div className="text-lg sm:text-xl md:text-3xl font-bold text-blue-600">5000+</div>
          <div className="text-xs sm:text-sm md:text-base text-gray-600">واجب مكتمل</div>
        </div>
        <div className="space-y-1">
          <div className="text-lg sm:text-xl md:text-3xl font-bold text-green-600">1200+</div>
          <div className="text-xs sm:text-sm md:text-base text-gray-600">جلسة اختبار</div>
        </div>
        <div className="space-y-1">
          <div className="text-lg sm:text-xl md:text-3xl font-bold text-purple-600">99%</div>
          <div className="text-xs sm:text-sm md:text-base text-gray-600">رضا الطلاب</div>
        </div>
      </div>
    </div>
  );
};